/**
 * SchoolShareCard — generates a QR + printable poster that links parents
 * directly to this school's public admissions form.
 *
 * The QR encodes the canonical share URL `<origin>/s/<slug>?redirect=/school/<tid>/admissions/apply`
 * — the slug page resolves to the tenant and then forwards to the public
 * admissions form. Falls back to the direct `/school/<tid>/admissions/apply`
 * URL when the school has no slug yet.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Share2, Download, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import { QRCodeCanvas } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolShareCard({ tenantId }) {
  const [generating, setGenerating] = useState(false);
  const qrRef = React.useRef(null);

  const { data: tenant } = useQuery({
    queryKey: ['school-tenant', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}`, { headers: authHeaders() });
      return r.ok ? r.json() : {};
    },
    enabled: !!tenantId,
  });

  const slug = tenant?.slug || '';
  const shareUrl = slug
    ? `${window.location.origin}/s/${slug}?redirect=/school/${tenantId}/admissions/apply`
    : `${window.location.origin}/school/${tenantId}/admissions/apply`;

  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied');
  };

  // Render QR via the hidden QRCodeCanvas, then download as PNG.
  const downloadPng = async () => {
    setGenerating(true);
    try {
      const canvas = qrRef.current?.querySelector('canvas');
      if (!canvas) { toast.error('QR not ready yet — try again'); return; }
      canvas.toBlob((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${tenant?.name || 'school'}-admissions-qr.png`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success('QR PNG downloaded');
      }, 'image/png');
    } finally {
      setGenerating(false);
    }
  };

  // Open a printable poster (A5) in a new tab.
  const printPoster = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) { toast.error('QR not ready yet — try again'); return; }
    const dataUrl = canvas.toDataURL('image/png');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${tenant?.name || 'School'} — Admissions QR</title>
<style>
  @media print { @page { size: A5; margin: 0; } body { margin: 0; } .noprint { display: none; } }
  body { font-family: -apple-system, system-ui, sans-serif; padding: 32px; max-width: 600px; margin: 0 auto; }
  .poster { border: 2px solid #1e293b; border-radius: 16px; padding: 32px; text-align: center; }
  .qr { width: 320px; height: 320px; margin: 24px auto; display: block; }
  h1 { font-size: 28px; margin: 0 0 8px; color: #1e293b; }
  h2 { font-size: 16px; color: #475569; margin: 0; font-weight: 500; }
  p { font-size: 13px; color: #64748b; }
  .url { font-family: ui-monospace, monospace; font-size: 11px; word-break: break-all; color: #6366f1; }
  .cta { background: #1e293b; color: white; padding: 12px 24px; border-radius: 8px; display: inline-block; margin-top: 8px; font-weight: 600; }
  button { padding: 10px 16px; border-radius: 8px; border: 1px solid #e2e8f0; background: #f8fafc; cursor: pointer; }
</style></head><body>
<div class="poster">
  <h1>${tenant?.name || 'Apply now'}</h1>
  <h2>Scan to apply for admission</h2>
  <img class="qr" src="${dataUrl}" alt="Admissions QR" />
  <span class="cta">Scan with any phone camera</span>
  <p>Or visit:<br/><span class="url">${shareUrl}</span></p>
</div>
<div class="noprint" style="text-align:center;margin-top:16px;">
  <button onclick="window.print()">Print this poster</button>
</div>
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
    win.document.write(html);
    win.document.close();
    toast.success('Poster opened in new tab');
  };

  return (
    <Card data-testid="school-share-card" className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Share2 className="h-4 w-4 text-indigo-500" /> Share & admissions QR
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Print or share this QR code on banners, flyers, and reception desks. Parents scan with their phone camera and land directly on this school's admissions form.
        </p>
        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
          <code className="text-xs flex-1 truncate" data-testid="share-url-preview">{shareUrl}</code>
          <Button variant="ghost" size="sm" onClick={copy} data-testid="share-copy-btn">
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadPng} disabled={generating} size="sm" data-testid="share-download-btn">
            {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Download QR PNG
          </Button>
          <Button onClick={printPoster} variant="outline" size="sm" data-testid="share-print-btn">
            Print A5 poster
          </Button>
        </div>
        {/* Hidden QR canvas — referenced by the download/print handlers. */}
        <div ref={qrRef} className="hidden">
          <QRCodeCanvas value={shareUrl} size={600} level="M" includeMargin />
        </div>
      </CardContent>
    </Card>
  );
}
